import { type ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { theme } from '@styles/theme';

interface StaggerContainerProps {
  children: ReactNode;
  /** Delay entre cada filho. */
  staggerDelay?: number;
  /** Delay antes de começar a sequência. */
  initialDelay?: number;
  /** Quanto do container precisa estar visível. */
  threshold?: number;
  once?: boolean;
  className?: string;
}

/**
 * Anima seus filhos diretos em cascata quando entra no viewport.
 * Os filhos precisam ter `variants` compatíveis — ou usar `<StaggerItem>`.
 *
 * @example
 * <StaggerContainer>
 *   <StaggerItem><Card /></StaggerItem>
 *   <StaggerItem><Card /></StaggerItem>
 *   <StaggerItem><Card /></StaggerItem>
 * </StaggerContainer>
 */
export function StaggerContainer({
  children,
  staggerDelay = 0.1,
  initialDelay = 0,
  threshold = 0.1,
  once = true,
  className,
}: StaggerContainerProps) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: threshold }}
      variants={{
        visible: {
          transition: {
            delayChildren: initialDelay,
            staggerChildren: shouldReduceMotion ? 0 : staggerDelay,
          },
        },
        hidden: {},
      }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Item de um StaggerContainer. Gerencia sua própria animação de entrada.
 */
export function StaggerItem({
  children,
  className,
  direction = 'up',
  distance = 32,
}: {
  children: ReactNode;
  className?: string;
  direction?: 'up' | 'left' | 'none';
  distance?: number;
}) {
  function hidden() {
    if (direction === 'none') return { opacity: 0 };
    if (direction === 'left') return { opacity: 0, x: -distance };
    return { opacity: 0, y: distance };
  }

  return (
    <motion.div
      className={className}
      variants={{
        hidden: hidden(),
        visible: {
          opacity: 1,
          x: 0,
          y: 0,
          transition: {
            duration: theme.durations.slower,
            ease: theme.easings.smoothOut,
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
}
